import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal';
import {
  ArrowLeft,
  Trash2,
  Edit,
  Check,
  X,
  Plus,
  ChevronUp,
  ChevronDown,
  Upload,
  CheckCircle,
  AlertTriangle,
  ImagePlus,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import {
  AGENT_DEPARTMENTS,
  AGENT_SECTIONS,
  getAgentTemplate,
  updateAgentTemplate,
  uploadAgentGif,
  removeAgentGif,
  saveAgentTemplateVersion,
  newAgentItemId,
} from '../../services/agentTemplatesService';
import { listClaudeSkills } from '../../services/claudeSkillsService';
import { suggestNextVersion } from '../../services/hrCollectionsService';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const isUrl = (s) => /^https?:\/\//i.test(String(s || '').trim());

/**
 * One of the five agent lists (routines, abilities, library, skills, examples).
 * View mode shows title + detail rows; edit mode edits the draft list in place.
 * The Skills list can also link an existing Skills catalog entry.
 */
const AgentSectionCard = ({ section, items, editing, onChange, skillsCatalog = [] }) => {
  const { darkMode } = useTheme();
  const [newTitle, setNewTitle] = useState('');
  const [pickSkillId, setPickSkillId] = useState('');
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const fieldClass = `w-full rounded-md border px-3 py-1.5 text-sm ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const iconBtn = `p-1 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`;

  const patch = (id, fields) => onChange(items.map((it) => (it.id === id ? { ...it, ...fields } : it)));
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const add = () => {
    const title = newTitle.trim();
    if (!title) return;
    onChange([...items, { id: newAgentItemId(), title, detail: '' }]);
    setNewTitle('');
  };
  const linkSkill = () => {
    const skill = skillsCatalog.find((s) => s.id === pickSkillId);
    if (!skill) return;
    onChange([...items, { id: newAgentItemId(), title: skill.title || 'Skill', detail: skill.description || '', skillId: skill.id }]);
    setPickSkillId('');
  };
  const linkedIds = new Set(items.map((it) => it.skillId).filter(Boolean));

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
      <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <span className="mr-1.5">{section.emoji}</span>
        {section.label}
      </h2>

      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={item.id} className="flex items-start gap-3 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
            <span className={`shrink-0 text-xs font-semibold w-5 text-center pt-1 ${subtle}`}>{index + 1}.</span>
            {editing ? (
              <div className="flex-1 min-w-0 space-y-1.5">
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => patch(item.id, { title: e.target.value })}
                  placeholder={section.titlePlaceholder}
                  className={fieldClass}
                />
                <textarea
                  value={item.detail}
                  onChange={(e) => patch(item.id, { detail: e.target.value })}
                  placeholder={section.detailPlaceholder}
                  rows={section.key === 'examples' ? 3 : 2}
                  className={fieldClass}
                />
                {item.skillId && <p className={`text-[11px] ${subtle}`}>Linked to the Skills catalog</p>}
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {item.title}
                  {item.skillId && (
                    <span className="ml-2 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      Skills catalog
                    </span>
                  )}
                </p>
                {item.detail &&
                  (isUrl(item.detail) ? (
                    <a
                      href={item.detail.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline break-all"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" /> {item.detail.trim()}
                    </a>
                  ) : (
                    <p className={`text-xs mt-0.5 whitespace-pre-line ${subtle}`}>{item.detail}</p>
                  ))}
              </div>
            )}
            {editing && (
              <div className="flex items-center shrink-0">
                <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className={iconBtn} title="Move up">
                  <ChevronUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  className={iconBtn}
                  title="Move down"
                >
                  <ChevronDown size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(items.filter((it) => it.id !== item.id))}
                  className="p-1 text-red-400 hover:text-red-300"
                  title="Remove"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        ))}

        {items.length === 0 && <p className={`text-sm text-center py-6 ${subtle}`}>No {section.label.toLowerCase()} yet</p>}

        {editing && (
          <div className="flex items-center gap-2 pt-1">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder={section.titlePlaceholder}
              className={fieldClass}
            />
            <button
              type="button"
              onClick={add}
              disabled={!newTitle.trim()}
              className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Plus size={16} /> Add
            </button>
          </div>
        )}

        {editing && section.key === 'skills' && skillsCatalog.length > 0 && (
          <div className="flex items-center gap-2">
            <select value={pickSkillId} onChange={(e) => setPickSkillId(e.target.value)} className={fieldClass}>
              <option value="">Link a skill from the Skills catalog…</option>
              {skillsCatalog
                .filter((s) => !linkedIds.has(s.id))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={linkSkill}
              disabled={!pickSkillId}
              className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Plus size={16} /> Link
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const draftFrom = (agent) => ({
  name: agent.name || '',
  description: agent.description || '',
  category: agent.category || 'lead',
  ...AGENT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: agent[s.key] || [] }), {}),
});

/**
 * CRM overview for one agent template. Mirrors AutomationTemplateOverview:
 * back arrow, centered title with the display GIF + version chip, Update
 * version / Edit actions, then Description and the five agent lists.
 */
const AgentTemplateOverview = () => {
  const { agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [skillsCatalog, setSkillsCatalog] = useState([]);

  const [gifBusy, setGifBusy] = useState(false);
  const gifInputRef = useRef(null);

  // New-version modal
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionNumber, setVersionNumber] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [versionError, setVersionError] = useState('');
  const [versionSaving, setVersionSaving] = useState(false);

  const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'BBA Team';
  const user = { uid: currentUser?.uid, name: userName };
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const chipGood =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const cardClass = 'bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const row = await getAgentTemplate(agentId);
        if (cancelled) return;
        setAgent(row);
        setDraft(draftFrom(row));
        // ?edit=1 (from Add agent) opens straight into edit mode
        if (searchParams.get('edit') === '1') {
          setIsEditing(true);
          setSearchParams({}, { replace: true });
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching agent:', err);
        if (!cancelled) setError('Failed to load agent details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  useEffect(() => {
    listClaudeSkills()
      .then((rows) => setSkillsCatalog(rows.filter((r) => r.title).sort((a, b) => a.title.localeCompare(b.title))))
      .catch(() => {});
  }, []);

  // ---- Edit / save ----
  const handleSave = async () => {
    if (!draft.name.trim()) {
      setSaveError('Enter an agent name.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const saved = await updateAgentTemplate(agentId, draft);
      setAgent((prev) => ({ ...prev, ...saved }));
      setDraft((prev) => ({ ...prev, ...saved }));
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating agent:', err);
      setSaveError(err.message || 'Could not save the agent.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(draftFrom(agent));
    setSaveError('');
    setIsEditing(false);
  };

  // ---- GIF (saves immediately, like the automation display emoji) ----
  const handleGifFile = async (file) => {
    if (!file) return;
    setGifBusy(true);
    setSaveError('');
    try {
      const gif = await uploadAgentGif(agentId, file, user);
      setAgent((prev) => ({ ...prev, gif }));
    } catch (err) {
      console.error('GIF upload failed:', err);
      setSaveError(err.message || 'Could not upload the GIF.');
    } finally {
      setGifBusy(false);
    }
  };

  const handleRemoveGif = async () => {
    setGifBusy(true);
    try {
      await removeAgentGif(agentId);
      setAgent((prev) => ({ ...prev, gif: null }));
    } catch (err) {
      console.error('GIF remove failed:', err);
    } finally {
      setGifBusy(false);
    }
  };

  // ---- Versioning ----
  const openVersionModal = () => {
    setVersionNumber(suggestNextVersion(agent.version));
    setVersionNotes('');
    setVersionError('');
    setVersionOpen(true);
  };

  const submitVersion = async () => {
    if (!versionNumber.trim()) {
      setVersionError('Enter a version number.');
      return;
    }
    setVersionSaving(true);
    setVersionError('');
    try {
      await saveAgentTemplateVersion({ agentId, version: versionNumber, notes: versionNotes, user });
      setVersionOpen(false);
      setAgent((prev) => ({ ...prev, version: versionNumber.trim() }));
    } catch (err) {
      console.error('Version save failed:', err);
      setVersionError(err.message || 'Could not save the version.');
    } finally {
      setVersionSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading agent details...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className={`text-center ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
          <p>{error || 'Failed to load agent details'}</p>
          <button
            onClick={() => navigate('/crm/agent-templates')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Back to Agents
          </button>
        </div>
      </div>
    );
  }

  const view = isEditing ? draft : agent;
  const department = AGENT_DEPARTMENTS.find((d) => d.id === view.category);

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative">
        {/* Header — back arrow, centered GIF + title + version, actions, all on one row */}
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/crm/agent-templates')}
              title="Back to Agents"
              className={`shrink-0 p-2 rounded-full transition-colors ${
                darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              <ArrowLeft size={22} />
            </button>

            <div className="flex-1 text-center min-w-0">
              <div className="flex items-center justify-center gap-3">
                {isEditing ? (
                  <input
                    type="text"
                    className={`text-2xl font-bold border-b ${
                      darkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'
                    } focus:border-purple-500 focus:outline-none text-center bg-transparent`}
                    value={draft.name}
                    onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                  />
                ) : (
                  <h1 className="text-2xl font-bold tracking-tight truncate text-gray-900 dark:text-white">{agent.name}</h1>
                )}
                <button
                  type="button"
                  onClick={openVersionModal}
                  title="Update the version"
                  className={`${chipGood} hover:opacity-80 transition-opacity`}
                >
                  <CheckCircle className="w-3 h-3" /> v{agent.version || '1.0'}
                </button>
              </div>
            </div>

            <div className="flex space-x-2 shrink-0">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60 flex items-center"
                  >
                    {saving ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Check size={16} className="mr-1" />}
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className={`px-3 py-1 ${
                      darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    } rounded-md flex items-center`}
                  >
                    <X size={16} className="mr-1" />
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={openVersionModal}
                    className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
                    title="Save the current state as a new version"
                  >
                    <Upload size={16} className="mr-1" />
                    Update version
                  </button>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <Edit size={16} className="mr-1" />
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
          {saveError && (
            <p className="mt-3 text-sm text-red-600 flex items-center justify-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {saveError}
            </p>
          )}
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* GIF + department */}
          <div className="space-y-6">
            <div className={cardClass}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Agent GIF</h2>
              <button
                type="button"
                onClick={() => gifInputRef.current?.click()}
                disabled={gifBusy}
                title={agent.gif?.url ? 'Replace the GIF' : 'Upload a GIF'}
                className={`w-full aspect-square rounded-xl overflow-hidden flex flex-col items-center justify-center gap-2 transition-colors ${
                  agent.gif?.url
                    ? ''
                    : darkMode
                    ? 'border-2 border-dashed border-gray-600 text-gray-400 hover:border-gray-500'
                    : 'border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400'
                }`}
              >
                {gifBusy ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : agent.gif?.url ? (
                  <img src={agent.gif.url} alt={agent.name} className="w-full h-full object-cover" />
                ) : (
                  <>
                    <ImagePlus className="w-8 h-8" />
                    <span className="text-sm font-medium">Upload a GIF</span>
                  </>
                )}
              </button>
              <input
                ref={gifInputRef}
                type="file"
                accept="image/gif,.gif"
                className="hidden"
                onChange={(e) => {
                  handleGifFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              {agent.gif?.url && (
                <div className="flex justify-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => gifInputRef.current?.click()}
                    disabled={gifBusy}
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-purple-600 hover:bg-purple-700"
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveGif}
                    disabled={gifBusy}
                    className="px-3 py-1.5 rounded-md text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className={cardClass}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Department</h2>
              {isEditing ? (
                <select
                  value={draft.category}
                  onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value }))}
                  className={inputClass}
                >
                  {AGENT_DEPARTMENTS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className={`text-center text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {department ? `${department.emoji} ${department.label}` : '—'}
                </p>
              )}
            </div>
          </div>

          {/* Description + the five agent lists */}
          <div className="lg:col-span-2 space-y-6">
            <div className={cardClass}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Description</h2>
              {isEditing ? (
                <textarea
                  className={inputClass}
                  rows={4}
                  value={draft.description}
                  onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                />
              ) : agent.description ? (
                <p className={`whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{agent.description}</p>
              ) : (
                <p className={`text-sm text-center ${subtle}`}>No description yet</p>
              )}
            </div>

            {AGENT_SECTIONS.map((section) => (
              <AgentSectionCard
                key={section.key}
                section={section}
                items={view[section.key] || []}
                editing={isEditing}
                onChange={(items) => setDraft((prev) => ({ ...prev, [section.key]: items }))}
                skillsCatalog={skillsCatalog}
              />
            ))}
          </div>
        </div>

        {/* New version modal */}
        <Modal isOpen={versionOpen} onClose={() => !versionSaving && setVersionOpen(false)} maxWidth="md">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Save new version</h2>
          <p className={`text-sm mb-4 ${subtle}`}>{agent.name}</p>

          <label className={labelClass}>
            Version number <span className={`font-normal ${subtle}`}>(auto-suggested)</span>
          </label>
          <input
            type="text"
            value={versionNumber}
            onChange={(e) => setVersionNumber(e.target.value)}
            placeholder="e.g. 1.1, 2.0"
            disabled={versionSaving}
            className={`${inputClass} mb-4`}
          />

          <label className={labelClass}>Change notes (optional)</label>
          <textarea
            value={versionNotes}
            onChange={(e) => setVersionNotes(e.target.value)}
            rows={2}
            placeholder="What changed in this version?"
            disabled={versionSaving}
            className={`${inputClass} mb-2`}
          />
          <p className={`text-xs mb-4 ${subtle}`}>
            Snapshots the agent's current name, description, GIF, routines, abilities, library, skills, and examples.
            Prior versions stay available in the history.
          </p>

          {versionError && (
            <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {versionError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setVersionOpen(false)}
              disabled={versionSaving}
              className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              onClick={submitVersion}
              disabled={versionSaving || !versionNumber.trim()}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {versionSaving ? 'Saving...' : 'Save version'}
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AgentTemplateOverview;
